/**
 * iter88.b (Item AA · REBUILD) — Dashboard overview.
 *
 * Founder feedback on the first bento pass: size followed decoration, not
 * content. Boxes were large because a "bento grid" felt trendy, not because
 * they carried enough information to earn the space. This rebuild throws
 * that out and applies the founder's actual principles:
 *
 *   1. Size follows content. If a fact is one number, it renders as one
 *      number - no oversized hero card padded with air.
 *   2. ONE focal point. The next real, actionable deadline gets stronger
 *      type weight + higher contrast than anything else. Everything else
 *      recedes.
 *   3. Gestalt proximity. Time-related items (deadline + days-to-go +
 *      packing readiness) cluster tightly. Unrelated items (peers, cost
 *      of living, weekend travel) sit in separate groups with real space
 *      between them.
 *   4. F-pattern. Most urgent info top-left / top-first. Priority is not
 *      chosen to fit a cell shape.
 *   5. Chunking. Three clearly distinct groups (Timeline · People · Places),
 *      not six equally-weighted tiles.
 *   6. Type tuned for skimming, above WCAG AA. Primary text is text-ink
 *      (near-black, ~13:1 vs paper), muted is text-ink/70 (~5.5:1),
 *      generous leading throughout.
 *   7. Zero new colors - only existing --moe-* CSS vars.
 *
 * If this ends up looking like a clean hierarchy of three grouped sections
 * rather than a "bento grid" - that's the point. Cognitive principles over
 * layout trend names.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Coins, Map as MapIcon, Package as PackageIcon, Users } from "lucide-react";

export default function DashboardOverview({ c, manifest, packing, tasks }) {
  const daysToGo = daysUntil(c?.start_date);
  const nextTask = pickNextTask(tasks);
  const peers    = pickPeers(manifest);

  return (
    <section
      data-testid="dashboard-overview"
      aria-label="At a glance"
      className="mt-8"
    >
      {/* ================================================================
          GROUP A · TIMELINE - the single most important cluster on the page.
          Focal point (the next deadline headline) uses the strongest type
          + highest contrast. Days-to-go is small, confident, kerned - it's
          a supporting fact, not a hero. Packing readiness sits at the tail
          because it's also time-relative but never the primary ask.
          ============================================================= */}
      <div
        data-testid="overview-timeline"
        className="rounded-2xl border border-ink/12 bg-white p-6 md:p-8"
      >
        {/* Tiny eyebrow · same style as other section eyebrows */}
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">
          Timeline
        </div>

        {/* FOCAL POINT · next deadline, if any. Bigger, bolder, higher
            contrast than anything else on the page. This is the "look here
            first" element. */}
        {nextTask ? (
          <a
            href="#dash-tasks-framing"
            data-testid="overview-next-deadline"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("dash-tasks-framing")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="mt-3 block group"
          >
            <div className="text-[13px] text-ink/70 font-semibold">Next deadline</div>
            <div className="mt-1 text-2xl md:text-[28px] font-bold tracking-tight leading-tight text-ink group-hover:text-elec transition-colors">
              {nextTask.task_name}
            </div>
            <div className="mt-2 text-[14px] text-ink/80">
              Due <b className="text-ink">{fmtDate(nextTask.deadline_date)}</b>
              <span className="text-ink/50"> · </span>
              <span>{humanRel(nextTask.deadline_date)}</span>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-elec opacity-90 group-hover:opacity-100">
              See tasks <ArrowRight size={12}/>
            </div>
          </a>
        ) : (
          <div data-testid="overview-no-deadline" className="mt-3">
            <div className="text-[13px] text-ink/70 font-semibold">Next deadline</div>
            <div className="mt-1 text-lg font-bold text-ink">Nothing due right now.</div>
            <div className="mt-1 text-[13px] text-ink/70">Your task board below is fully clear.</div>
          </div>
        )}

        {/* Supporting facts row · small, confident, tabular. Sits inside
            the SAME timeline group because both facts are time-context for
            the deadline above. Divider on md+ so the eye still reads them
            as one continuous cluster. */}
        <div className="mt-6 pt-5 border-t border-ink/10 flex flex-wrap gap-x-8 gap-y-3 text-[13px] text-ink/80">
          {daysToGo != null && (
            <div data-testid="overview-days-to-go">
              <span className="tabular-nums font-bold text-ink text-lg mr-1.5">{daysToGo}</span>
              days until you land in <b className="text-ink">{c?.host_city || "your host city"}</b>
              {c?.start_date && <> · <span className="text-ink/60">{fmtDate(c.start_date)}</span></>}
            </div>
          )}
          {packing && packing.profile_complete && (
            <Link
              to="/dashboard/packing"
              data-testid="overview-packing"
              className="text-ink/80 hover:text-elec transition-colors"
            >
              <span className="tabular-nums font-bold text-ink text-lg mr-1.5">{packing.readiness_pct}%</span>
              packing ready
              {packing.essential_remaining > 0 && <> · <span>{packing.essential_remaining} essential left</span></>}
            </Link>
          )}
          {packing && !packing.profile_complete && (
            <Link
              to="/dashboard/packing"
              data-testid="overview-packing"
              className="text-elec font-semibold hover:underline"
            >
              Set up your packing list &rarr;
            </Link>
          )}
        </div>
      </div>

      {/* ================================================================
          GROUP B + C · secondary quick-jump rows.
          Each is a single compact line with one label + link. Same
          Gestalt principle: unrelated items get real space between them
          via section-level margin, not via decorative cards.
          These are intentionally UNDERWEIGHTED vs the timeline block
          above - they are shortcuts, not primary content.
          ============================================================= */}
      <div className="mt-6 grid gap-3 md:grid-cols-3" data-testid="overview-quicklinks">
        <QuickLink
          testid="overview-peers"
          to={peers ? "/dashboard/manifest?tab=matches" : "/dashboard/manifest"}
          Icon={Users}
          eyebrow="People"
          label={peers ? `${peers.n} ${peers.n === 1 ? "peer" : "peers"} ${peers.scope}` : "Meet your cohort before you land"}
          hint={peers ? "Cohort" : "Opt in to Cohort"}
        />
        <QuickLink
          testid="overview-cost"
          to="/dashboard/cost-estimator"
          Icon={Coins}
          eyebrow="Practical"
          label={`A month in ${c?.host_city || "your host city"}`}
          hint="Cost of living"
        />
        <QuickLink
          testid="overview-roam"
          to="/dashboard/roam"
          Icon={MapIcon}
          eyebrow="Practical"
          label={`Weekend trips around ${c?.host_city || "your host city"}`}
          hint="Drift"
        />
      </div>
    </section>
  );
}

/* --- one small link row primitive · under-weighted on purpose ------------ */
function QuickLink({ testid, to, Icon, eyebrow, label, hint }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center gap-3 px-4 py-3 rounded-lg border border-ink/10 hover:border-ink/25 hover:bg-ink/[0.02] active:bg-ink/[0.04] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-elec/60 focus-visible:ring-offset-2"
    >
      <Icon size={16} strokeWidth={2} className="text-elec shrink-0"/>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-ink/60 font-bold">{eyebrow}</div>
        <div className="mt-0.5 text-[13px] font-semibold text-ink leading-snug truncate">{label}</div>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-ink/50 font-bold shrink-0 group-hover:text-elec transition-colors">
        {hint} <ArrowRight size={10} className="inline align-baseline"/>
      </div>
    </Link>
  );
}

/* ---------- helpers ---------------------------------------------------- */
function daysUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((t - Date.now()) / 86400000));
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toDateString().slice(4); } catch { return iso; }
}
function humanRel(iso) {
  const d = daysUntil(iso);
  if (d == null) return "";
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d < 14)  return `in ${d} days`;
  if (d < 60)  return `in ${Math.round(d/7)} weeks`;
  return `in ${Math.round(d/30)} months`;
}
function pickNextTask(tasks) {
  if (!Array.isArray(tasks)) return null;
  return tasks
    .filter(t => t?.status !== "completed" && t?.deadline_date)
    .map(t => ({ ...t, _d: new Date(t.deadline_date).getTime() }))
    .filter(t => Number.isFinite(t._d))
    .sort((a, b) => a._d - b._d)[0] || null;
}
function pickPeers(manifest) {
  if (!manifest?.opted_in) return null;
  const n = manifest.same_university_count > 0
    ? manifest.same_university_count : (manifest.matches_count || 0);
  if (!n) return null;
  const scope = manifest.same_university_count > 0 ? "at your host uni" : "in your cohort";
  return { n, scope };
}
